import { useCallback, useRef, useState, type ChangeEvent } from 'react';
import { SearchIcon, XIcon } from 'lucide-react';
import { Input } from '@/components/ui/input.tsx';
import { useDebouncedCallback } from '@/hooks/use-debounced-callback.ts';
import { cn } from '@/utils/utils.ts';

type SearchInputProps = {
  value: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  debounceMs?: number;
  className?: string;
};

export const SearchInput = ({
  value,
  onValueChange,
  placeholder = 'Search...',
  debounceMs = 300,
  className,
}: SearchInputProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [displayValue, setDisplayValue] = useState(value);
  const [prevValue, setPrevValue] = useState(value);
  const debouncedOnChange = useDebouncedCallback(onValueChange, debounceMs);

  if (prevValue !== value) {
    setPrevValue(value);
    setDisplayValue(value);
    debouncedOnChange.cancel();
  }

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setDisplayValue(event.target.value);
      debouncedOnChange(event.target.value);
    },
    [debouncedOnChange]
  );

  const handleClear = useCallback(() => {
    debouncedOnChange.cancel();
    setDisplayValue('');
    onValueChange('');
    inputRef.current?.focus();
  }, [debouncedOnChange, onValueChange]);

  return (
    <div className={cn('relative', className)}>
      <SearchIcon className="text-muted-foreground absolute top-2 left-2.5 size-4" />
      <Input
        ref={inputRef}
        className="h-8 bg-white pr-8 pl-8"
        placeholder={placeholder}
        value={displayValue}
        onChange={handleChange}
      />
      {displayValue && (
        <button
          title="Clear"
          type="button"
          className="text-muted-foreground absolute top-0 right-0 cursor-pointer p-2"
          onClick={handleClear}
        >
          <XIcon className="size-4" />
        </button>
      )}
    </div>
  );
};
